import { CSSObject } from "@mui/system"
import { alpha } from "@mui/material/styles"
import * as React from "react"
import IconButton from "@mui/material/IconButton"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import NextLink from "next/link"
import { useRouter } from "next/router"
import scss from "./SideMenu.module.scss"
import Typography from "@mui/material/Typography"

import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Theme, useMediaQuery, useTheme } from "@mui/material"
import { signOut, useSession } from "next-auth/react"
import { useTranslation } from "@/contexts/LanguageContext"
import { APP_MENU_ITEMS } from "@/constants/menuItems"
import { useUserPreferences } from "@/hooks/useUserPreferences"
import { isSessionAdmin } from "@/utils/auth"

const closedDrawerWidth = 72
const openDrawerWidth = 264

// Fixed accent for the active sidebar item - theme.palette.primary.main isn't
// reliably blue across this app's theme variants (e.g. lightTheme resolves it
// to a near-black), so the active state uses this exact blue regardless of
// theme/mode instead.
const ACTIVE_ITEM_COLOR = "#224375"

// Matches exactly what Header.tsx's AppBar actually renders: the theme's
// shared `frostedBackdrop` (background/blur/border/borderRadius, see
// src/theme/lightTheme.ts + darkTheme.ts MuiAppBar override) with boxShadow
// forced to "none", same as Header.tsx's own sx override does. Kept as one
// object per theme mode instead of re-deriving from the theme file, since
// MUI doesn't expose a component's resolved styleOverrides for reuse here.
//
// Tried adding a harder theme.palette.divider line on the right edge (to
// mirror Header.tsx's explicit <Divider /> below its Toolbar) - it's a much
// more opaque grey than the soft frosted rgba border used everywhere else,
// so it read as a thick, out-of-place line rather than a subtle edge.
// Reverted: the uniform soft border below already covers all sides,
// including the right edge, consistently with the rest of the surface.
const headerLikeSurface = (theme: Theme): CSSObject =>
  theme.palette.mode === "dark"
    ? { backgroundColor: "rgba(17, 25, 40, 0.72)", border: `1px solid ${theme.palette.divider}` }
    : { backgroundColor: "rgba(255, 255, 255, 0.6)", border: `1px solid ${theme.palette.divider}` }

// Same soft frosted shadow the theme gives its other surfaces (lightTheme.ts / darkTheme.ts
// `frostedBackdrop`) - Header.tsx's AppBar forces boxShadow to "none", so this is the shadow
// the rest of the app's surfaces share rather than one copied from the header itself.
const headerLikeShadow = (theme: Theme): string =>
  theme.palette.mode === "dark" ? "0 20px 50px -28px rgba(15, 23, 42, 0.65)" : "0 18px 35px -25px rgba(15, 23, 42, 0.45)"

// Thinner scrollbar for the panel's own internal scroll (see overflowY:
// "auto" below) - standard `scrollbar-width` for Firefox/modern Chromium,
// plus the ::-webkit-scrollbar pseudo-elements for Safari/older Chromium,
// per the project's "must work on all browsers" requirement.
const thinScrollbar = (theme: Theme): CSSObject => {
  const thumbColor = theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.4)" : "rgba(15, 23, 42, 0.25)"
  return {
    scrollbarWidth: "thin",
    scrollbarColor: `${thumbColor} transparent`,
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { backgroundColor: thumbColor, borderRadius: "3px" }
  }
}

const openedMixin = (theme: Theme): CSSObject => ({
  width: openDrawerWidth,
  ...headerLikeSurface(theme),
  ...thinScrollbar(theme),
  padding: "0.35rem",
  borderRadius: "0 0 5px 5px",
  textAlign: "center",
  backdropFilter: "saturate(180%) blur(28px)",
  boxShadow: headerLikeShadow(theme),
  position: "fixed",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: "hidden",
  overflowY: "auto"
})

const closedMixin = (theme: Theme): CSSObject => ({
  width: closedDrawerWidth,
  ...headerLikeSurface(theme),
  ...thinScrollbar(theme),
  padding: "0.35rem",
  borderRadius: "0 0 5px 5px",
  textAlign: "center",
  backdropFilter: "saturate(180%) blur(28px)",
  boxShadow: headerLikeShadow(theme),
  position: "fixed",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: "hidden",
  overflowY: "auto"
})

const SideMenu = () => {
  const theme = useTheme()
  const router = useRouter()
  const [open, setOpen] = React.useState(false)
  const mobileCheck = useMediaQuery("(min-width: 600px)")
  const { data: session } = useSession()
  const { t } = useTranslation()
  const isAdmin = isSessionAdmin(session)

  const getStorageKey = React.useCallback(() => (session?.user?.email ? `sideMenuOpen:${session.user.email}` : "sideMenuOpen"), [session])

  React.useEffect(() => {
    if (typeof window === "undefined") return
    const stored = localStorage.getItem(getStorageKey())
    if (stored === "1") setOpen(true)
    if (stored === "0") setOpen(false)
  }, [getStorageKey])

  const { preferences } = useUserPreferences()
  const hiddenMenuItems = React.useMemo(() => new Set(preferences.hiddenMenuItems || []), [preferences.hiddenMenuItems])

  const menuItems = React.useMemo(() => {
    const roles = Array.isArray(session?.user?.roles) ? session.user.roles : []
    const rolesSet = new Set(roles)
    const hasCustomRoles = rolesSet.size > 0

    // Filter out admin-only items (they'll be shown in the user menu instead)
    // Also filter out user-settings (it's only accessible from the header user menu)
    const adminOnlyIds = new Set([
      "settings",
      "docs",
      "user-manual",
      "user-access",
      "automation",
      "user-settings",
      "job-monitoring",
      "job-toggles",
      "alfredo-settings",
      "alfredo-import-manager",
      "pipeline-diagnostics",
      "hot-deals-zones",
      "favorites",
      "disliked"
    ])

    return APP_MENU_ITEMS.filter((item) => {
      // Exclude admin-only items from side menu
      if (adminOnlyIds.has(item.id)) {
        return false
      }
      // Exclude globally-hidden items and user-hidden items
      if (item.hidden || hiddenMenuItems.has(item.id)) {
        return false
      }
      if (item.action === "signout") {
        return true
      }
      if (isAdmin) {
        return true
      }
      if (rolesSet.has(item.id)) {
        return true
      }
      if (!hasCustomRoles && !item.adminOnly) {
        return true
      }
      return false
    })
  }, [isAdmin, session, hiddenMenuItems])

  const handleDrawerToggle = () => {
    setOpen((prev) => {
      const next = !prev
      if (typeof window !== "undefined") {
        localStorage.setItem(getStorageKey(), next ? "1" : "0")
      }
      return next
    })
  }

  const handleListItemButtonClick = (action?: "signout") => {
    if (action === "signout") {
      signOut()
      return
    }
    if (mobileCheck) {
      return
    }
    setOpen(false)
    if (typeof window !== "undefined") {
      localStorage.setItem(getStorageKey(), "0")
    }
  }

  const panelTopOffset = 64
  const currentDrawerWidth = open ? openDrawerWidth : closedDrawerWidth
  const toggleButtonSize = 24

  return (
    <>
      {/* Collapse toggle - sits before the first menu item, straddling the
          panel's right edge 50/50 (half in, half out). It's rendered as a
          sibling of the Drawer (not a child) and positioned fixed to the
          viewport by its own left/top math, because the panel itself has
          overflowX: hidden and would clip anything overflowing its edge. */}
      <IconButton
        onClick={handleDrawerToggle}
        size="small"
        aria-label={open ? "Collapse menu" : "Expand menu"}
        sx={{
          position: "fixed",
          top: 74,
          left: currentDrawerWidth - toggleButtonSize / 2,
          transition: theme.transitions.create("left", {
            easing: theme.transitions.easing.sharp,
            duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen
          }),
          zIndex: theme.zIndex.drawer + 10,
          width: toggleButtonSize,
          height: toggleButtonSize,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          "&:hover": { bgcolor: "action.hover" }
        }}>
        {!open ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
      </IconButton>
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          width: open ? openDrawerWidth : closedDrawerWidth,
          // The outer root's width must animate in lockstep with the inner
          // .MuiDrawer-paper (below) - otherwise the root's layout box (which
          // reserves space for sibling content) snaps to its new width
          // instantly while the visible panel is still transitioning, showing
          // as a flash/rectangle that doesn't match the panel's shape.
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen
          }),
          // Must set BOTH axes explicitly - CSS computes an unset overflow-y as
          // "auto" whenever overflow-x is anything other than "visible", which
          // is exactly what turned this into a scrolling container (visible as
          // a persistent grey scrollbar) after overflowX: "hidden" alone was
          // added for the transition fix above.
          overflowX: "hidden",
          overflowY: "hidden",
          [`& .MuiDrawer-paper`]: {
            // Constrain to the visible viewport below the header (instead of
            // "auto", which just grows to fit every menu item) so the panel
            // scrolls its own contents - via overflowY in the mixins below -
            // rather than pushing the page itself to grow taller.
            height: `calc(100vh - ${panelTopOffset}px)`,
            left: 0,
            top: panelTopOffset,
            whiteSpace: "nowrap",
            ...(open && { ...openedMixin(theme), "& .MuiDrawer-paper": openedMixin(theme) }),
            ...(!open && { ...closedMixin(theme), "& .MuiDrawer-paper": closedMixin(theme) })
          }
        }}>
        <div className={scss.drawerContent}>
          <List sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
            {menuItems.map((item) => {
              const label = t(item.labelKey)
              const href = item.href
                ? item.href
                : item.route
                  ? item.route.startsWith("http")
                    ? item.route
                    : item.route.length > 0
                      ? `/features/${item.route}`
                      : "/features"
                  : "/features"
              const IconComponent = item.icon
              // Signout has no real destination (falls back to '/features' below just
              // to satisfy the href type) - it must never read as "active".
              // `asPath` too: the lifecycle stages share one page and differ only by `?stage=`.
              const isActive = item.action !== "signout" && (router.pathname === href || router.asPath === href)

              const button = (
                <ListItemButton
                  onClick={() => handleListItemButtonClick(item.action)}
                  title={label}
                  aria-label={label}
                  sx={{
                    justifyContent: open ? "initial" : "center",
                    px: 2,
                    py: 0.875,
                    borderRadius: "8px",
                    ...(isActive && {
                      bgcolor: alpha(ACTIVE_ITEM_COLOR, theme.palette.mode === "dark" ? 0.24 : 0.1),
                      "&:hover": { bgcolor: alpha(ACTIVE_ITEM_COLOR, theme.palette.mode === "dark" ? 0.32 : 0.16) }
                    })
                  }}>
                  <ListItemIcon
                    sx={{
                      py: 0,
                      minWidth: 0,
                      mr: open ? 1 : "auto",
                      justifyContent: "center",
                      color: isActive ? ACTIVE_ITEM_COLOR : theme.palette.text.primary
                    }}>
                    {React.createElement(IconComponent)}
                  </ListItemIcon>
                  <ListItemText>
                    <Typography
                      sx={{
                        fontSize: "11.5px",
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        color: isActive ? ACTIVE_ITEM_COLOR : theme.palette.text.primary,
                        opacity: open ? 1 : 0,
                        fontWeight: isActive ? 800 : 600
                      }}>
                      {label}
                    </Typography>
                  </ListItemText>
                </ListItemButton>
              )

              if (item.action === "signout") {
                return (
                  <ListItem key={item.labelKey} disablePadding sx={{ display: "block" }}>
                    {button}
                  </ListItem>
                )
              }

              return (
                <ListItem key={item.labelKey} disablePadding sx={{ display: "block" }}>
                  <NextLink className={scss.link} href={href}>
                    {button}
                  </NextLink>
                </ListItem>
              )
            })}
          </List>
        </div>
      </Drawer>
    </>
  )
}

export default SideMenu
